import { useEffect, useMemo, useState } from 'react'

import { useTheme } from '@mui/material'

import { UAInsightsDialogType } from '../types'

/**
 * The dialogs worth keeping in the tree: the one the URL points at, plus the
 * one it just replaced.
 *
 * Each dialog derives its own `open` flag from the URL, so unmounting the
 * moment the URL changes would cut MUI's exit transition short — and on a
 * dialog-to-dialog drilldown it would flash the page behind while the next
 * dialog fades in. Holding the outgoing key for the length of the leave
 * transition preserves the crossfade while keeping at most two dialogs
 * mounted instead of all fourteen.
 *
 * @param active The dialog key from the URL, or `null` when none is open.
 * @returns Keys to render, active first.
 */
export const useMountedDialogs = (active: UAInsightsDialogType | null): UAInsightsDialogType[] => {
  const { transitions } = useTheme()
  const leaveMs = transitions.duration.leavingScreen
  const [exiting, setExiting] = useState<UAInsightsDialogType | null>(null)
  const [seen, setSeen] = useState(active)

  // Adjusted during render rather than in an effect. Effects run after React
  // has committed, so an effect here would let the browser paint one frame
  // without the outgoing dialog: it unmounts, then remounts as a fresh
  // instance whose `open` is already `false` — a state it never transitioned
  // into, so MUI has nothing to animate. Deriving it here keeps the dialog in
  // the very commit that observes the change, which is what lets it animate
  // from open to closed.
  if (seen !== active) {
    setSeen(active)
    if (seen) setExiting(seen)
  }

  useEffect(() => {
    if (!exiting) return
    const timer = window.setTimeout(() => setExiting((current) => (current === exiting ? null : current)), leaveMs)
    return () => window.clearTimeout(timer)
  }, [exiting, leaveMs])

  return useMemo(() => {
    if (!active) return exiting ? [exiting] : []
    return exiting && exiting !== active ? [active, exiting] : [active]
  }, [active, exiting])
}
